import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import {
  ArrowLeft, RefreshCw, Loader2, Save, KeyRound, Trash2, Plus, Copy,
  AlertTriangle, Eye, EyeOff, Palette, Code,
} from 'lucide-react';
import { toast } from 'sonner';

export default function WorkspaceSettings() {
  const [branding, setBranding] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [showOnceKey, setShowOnceKey] = useState(null);
  const [revealKey, setRevealKey] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, k] = await Promise.all([
        api.get('/staff/workspace/branding'),
        api.get('/staff/api-keys'),
      ]);
      setBranding(b.data?.branding || {});
      setApiKeys(k.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBranding = async () => {
    setSaving(true);
    try {
      const r = await api.put('/staff/workspace/branding', branding);
      setBranding(r.data?.branding || branding);
      toast.success('Branding saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const createApiKey = async () => {
    if (!newKeyLabel.trim()) { toast.error('Label required'); return; }
    setCreatingKey(true);
    try {
      const r = await api.post('/staff/api-keys', { label: newKeyLabel.trim() });
      setShowOnceKey(r.data?.api_key);
      navigator.clipboard?.writeText(r.data?.api_key).catch(() => {});
      toast.success('Key created · copied to clipboard');
      setNewKeyLabel('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setCreatingKey(false); }
  };

  const revokeKey = async (id, label) => {
    if (!window.confirm(`Revoke API key "${label}"? Any partner using it will start getting 401s immediately.`)) return;
    try {
      await api.delete(`/staff/api-keys/${id}`);
      toast.success('Key revoked');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Revoke failed'); }
  };

  if (loading && !branding) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA' }}><Loader2 size={28} className="animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <div className="border-b border-gray-200 sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/staff/team" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="settings-back"><ArrowLeft size={16} /> Team</Link>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-500 ml-2">SETTINGS</span>
            </div>
          </div>
          <button onClick={load} className="p-2 text-gray-500 hover:text-gray-900" data-testid="settings-refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {/* BRANDING */}
        <section className="bg-white border border-gray-200" data-testid="branding-section">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <Palette size={14} className="text-gray-500" />
            <h2 className="text-[11px] font-bold tracking-widest uppercase text-gray-900">WORKSPACE BRANDING</h2>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-xs text-gray-500 leading-relaxed">Customize the name, colors, and tagline shown on public demo pages and outreach emails. Changes take effect everywhere immediately.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Workspace name" value={branding?.workspace_name || ''} onChange={(v) => setBranding((b) => ({ ...b, workspace_name: v }))} testId="brand-name" />
              <Field label="Tagline (1 sentence)" value={branding?.tagline || ''} onChange={(v) => setBranding((b) => ({ ...b, tagline: v }))} testId="brand-tagline" />
              <Field label="Logo · left text" value={branding?.logo_text_left || ''} onChange={(v) => setBranding((b) => ({ ...b, logo_text_left: v }))} testId="brand-logo-left" />
              <Field label="Logo · right text" value={branding?.logo_text_right || ''} onChange={(v) => setBranding((b) => ({ ...b, logo_text_right: v }))} testId="brand-logo-right" />
              <div className="sm:col-span-2">
                <label className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Accent color (hex, e.g. #FF2E00)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="text" value={branding?.accent_color || ''} onChange={(e) => setBranding((b) => ({ ...b, accent_color: e.target.value }))}
                    className="flex-1 p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors font-mono"
                    placeholder="#FF2E00" data-testid="brand-accent" />
                  <div className="w-12 h-12 border border-gray-200" style={{ background: branding?.accent_color || '#FF2E00' }} />
                </div>
              </div>
              <Field label="Public demo footer line" value={branding?.public_demo_footer || ''} onChange={(v) => setBranding((b) => ({ ...b, public_demo_footer: v }))} testId="brand-footer" extraClass="sm:col-span-2" />
            </div>
            {/* Live preview */}
            <div className="mt-2 border border-gray-200 bg-black p-5" data-testid="brand-preview">
              <div className="flex items-center gap-1 mb-2">
                <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: '#fff' }}>{branding?.logo_text_left || 'HUSCLE'}</span>
                <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: branding?.accent_color || '#FF2E00' }}>{branding?.logo_text_right || 'OS'}</span>
              </div>
              <p className="text-sm text-gray-300 leading-snug">{branding?.tagline || ''}</p>
              <p className="text-[10px] tracking-widest uppercase text-gray-500 mt-3">{branding?.public_demo_footer || ''}</p>
            </div>
            <button onClick={saveBranding} disabled={saving} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50 inline-flex items-center gap-2" data-testid="brand-save">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save branding
            </button>
          </div>
        </section>

        {/* API KEYS */}
        <section className="bg-white border border-gray-200" data-testid="api-keys-section">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <Code size={14} className="text-gray-500" />
            <h2 className="text-[11px] font-bold tracking-widest uppercase text-gray-900">PUBLIC API KEYS</h2>
          </div>
          <div className="p-5">
            <p className="text-xs text-gray-500 leading-relaxed mb-4">Give partners or your own internal tools read-only access. Each key works against <code className="bg-gray-100 px-1 py-0.5 text-[11px]">GET /api/v1/prospects</code>, <code className="bg-gray-100 px-1 py-0.5 text-[11px]">/api/v1/prospects/{'{id}'}</code>, and <code className="bg-gray-100 px-1 py-0.5 text-[11px]">/api/v1/conflicts</code>.</p>

            {/* Create new */}
            <div className="flex items-center gap-2 mb-4">
              <input value={newKeyLabel} onChange={(e) => setNewKeyLabel(e.target.value)} placeholder="Label this key (e.g. 'Zapier', 'Internal dashboard')"
                onKeyDown={(e) => e.key === 'Enter' && createApiKey()}
                className="flex-1 p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900" data-testid="api-key-label" />
              <button onClick={createApiKey} disabled={creatingKey} className="text-xs font-bold tracking-widest uppercase px-4 py-3 bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50 inline-flex items-center gap-2" data-testid="api-key-create">
                {creatingKey ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Generate
              </button>
            </div>

            {/* Show-once banner */}
            {showOnceKey && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200" data-testid="api-key-show-once">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-amber-900 mb-1">Save this key now — it won't be shown again.</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono break-all p-2 bg-white border border-amber-200">{revealKey ? showOnceKey : '•'.repeat(40)}</code>
                      <button onClick={() => setRevealKey(!revealKey)} className="p-2 border border-amber-200 hover:bg-amber-100 text-amber-700">{revealKey ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                      <button onClick={() => { navigator.clipboard?.writeText(showOnceKey); toast.success('Copied'); }} className="p-2 border border-amber-200 hover:bg-amber-100 text-amber-700"><Copy size={12} /></button>
                    </div>
                  </div>
                  <button onClick={() => setShowOnceKey(null)} className="text-amber-600 hover:text-amber-900 text-xs font-bold">✕</button>
                </div>
              </div>
            )}

            {/* Existing keys */}
            {apiKeys.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6 border border-gray-100 bg-gray-50">No API keys yet.</p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((k) => (
                  <div key={k.id} className={`p-3 border ${k.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`} data-testid={`api-key-row-${k.id}`}>
                    <div className="flex items-center gap-3">
                      <KeyRound size={14} className={k.is_active ? 'text-gray-500' : 'text-gray-300'} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-bold text-gray-900 truncate">{k.label}</p>
                          {!k.is_active && <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">REVOKED</span>}
                        </div>
                        <p className="text-[11px] text-gray-500 font-mono truncate">{k.preview}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Created {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'} by {k.created_by_name || '—'} · {k.call_count || 0} calls · last used {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'never'}</p>
                      </div>
                      {k.is_active && (
                        <button onClick={() => revokeKey(k.id, k.label)} className="text-[10px] font-bold tracking-widest uppercase px-2 py-1.5 border border-red-200 hover:bg-red-50 text-red-600 inline-flex items-center gap-1" data-testid={`api-key-revoke-${k.id}`}>
                          <Trash2 size={11} /> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, extraClass = '' }) {
  return (
    <div className={extraClass}>
      <label className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors" data-testid={testId} />
    </div>
  );
}
